import React from 'react';
import FormModal from '@frontend/Components/Modals/FormModal/FormModal';
import type { FleetPool, FleetUnit } from './referenceDataService';
import './BidAdminPage.css';

/**
 * The Units drawer (Build U, plan team-bid-parity-and-fleet-registry): one equipment
 * TYPE's actual fleet — every asset-tagged unit, its Active/Retired state, WHERE it is
 * (decision 8: an assignment on a phase whose derived status is not `complete`; a unit
 * whose every assignment is complete reads "in pool"), and its day-sheet evidence (zero
 * renders NOTHING — the panel idiom). Untagged quantity rows are DISCLOSED as a pool
 * line, never listed as units (decision 5).
 *
 * A bids-local overlay (decision 6): the shared Components/Table has no expandable rows
 * and legacy screens consume it, so this rides FormModal like CatalogFormModal does —
 * no shared-Table change, no new overlay CSS. Money-free: the registry read carries no
 * rate, and this drawer renders none.
 */
interface UnitsDrawerProps {
  typeName: string;
  units: FleetUnit[];
  pool?: FleetPool;
  isOpen: boolean;
  onClose: () => void;
  /** "Add unit" — the existing inventory CRUD, pre-filled with this type. */
  onAddUnit: () => void;
}

/** The location line: live assignments only; none live = "in pool". */
export const locationText = (unit: FleetUnit): string => {
  const live = unit.assignments.filter((a) => a.phase_status !== 'complete');
  if (live.length === 0) return 'in pool';
  return live
    .map((a) => `on ${a.matter_name || a.matterid} · ${a.phase_name || 'phase'}`)
    .join('; ');
};

/** Shared with the Fleet column (review fold) — one guard for the same value. */
export const poolQuantity = (pool: FleetPool): string => {
  const n = Number(pool.quantity);
  return Number.isFinite(n) ? String(n) : pool.quantity;
};

const UnitsDrawer: React.FC<UnitsDrawerProps> = ({
  typeName, units, pool, isOpen, onClose, onAddUnit,
}) => (
  <FormModal isOpen={isOpen} onClose={onClose} title={`Units — ${typeName}`} size="large">
    <div className="bid-admin-units">
      {units.length === 0 && (
        <p className="bid-admin-empty">
          No tagged units of this type yet — a unit is an asset-tagged inventory row.
        </p>
      )}

      {units.length > 0 && (
        <table className="bid-admin-units-table">
          <thead>
            <tr>
              <th>Asset tag</th>
              <th>Name</th>
              <th>Status</th>
              <th>Location</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <tr key={unit.id}>
                <td className="bid-admin-units-tag">{unit.asset_tag}</td>
                <td>{unit.name}</td>
                <td>
                  <span className={`bid-admin-badge ${unit.active ? 'is-on' : 'is-off'}`}>
                    {unit.active ? 'Active' : 'Retired'}
                  </span>
                </td>
                <td>{locationText(unit)}</td>
                <td className="bid-admin-units-evidence">
                  {unit.sheet_count > 0 && (
                    <>
                      seen on {unit.sheet_count} day sheet{unit.sheet_count === 1 ? '' : 's'}
                      {unit.last_seen ? ` · last ${unit.last_seen}` : ''}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pool && (
        <p className="bid-admin-units-pool">
          + {poolQuantity(pool)} untagged in pool
        </p>
      )}

      <div className="bid-admin-form-actions">
        <button type="button" className="bid-admin-btn is-primary" onClick={onAddUnit}>
          Add unit
        </button>
        <button type="button" className="bid-admin-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  </FormModal>
);

export default UnitsDrawer;
